import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api.js';

const CURRENCY_SYMBOL = { USD: '$', EUR: '€', SGD: 'S$', PHP: '₱' };

function money(amount, currency = 'USD') {
  const sym = CURRENCY_SYMBOL[currency] || '';
  const n = Number(amount || 0).toFixed(2);
  return `${sym}${n}`;
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/**
 * SaaS Provider Billing Desk (UC 4fc8ee00 / b1ff9920 / 8e813f5e).
 *
 * Internal, provider-only desk organised as three tabs (mirrors the design):
 *   1. Active Cycle — the monthly transaction-billing total (active per-use rate
 *      × completed tugboat deployments) with the itemized deployment log, CSV
 *      export, and invoice generation/dispatch.
 *   2. Historical Ledger — the dispatched-invoice delivery & SMTP tracking log.
 *   3. Rate Configuration — the per-tugboat-use rate rule (rate, currency,
 *      cut-off day, effective date, active), audited on save.
 *
 * The software usage fee is a provider operating expense (OPEX), strictly
 * segregated from customer-facing towage rate cards and SAP accounts receivable.
 */
export default function SaasBillingDeskPage() {
  const [activeTab, setActiveTab] = useState('active');

  const [usage, setUsage] = useState(null);
  const [period, setPeriod] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDetails, setShowDetails] = useState(false);

  const [invoices, setInvoices] = useState([]);
  const [histQuery, setHistQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('finance-ops@clienttug.com');
  const [emailError, setEmailError] = useState('');
  const [sending, setSending] = useState(false);
  const [invoiceMsg, setInvoiceMsg] = useState(null);

  // Rate Configuration tab state.
  const [cfg, setCfg] = useState({ baseRate: '', currency: 'USD', cutoffDay: 28, active: true, effectiveDate: '' });
  const [cfgMeta, setCfgMeta] = useState(null);
  const [cfgError, setCfgError] = useState('');
  const [cfgMsg, setCfgMsg] = useState(null);
  const [cfgSaving, setCfgSaving] = useState(false);

  const loadInvoices = useCallback(async () => {
    try {
      setInvoices(await api.getSaasInvoices());
    } catch {
      // delivery log is non-critical to the page render — ignore load failure.
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const s = await api.getSaasBillingSettings();
      setCfgMeta(s);
      setCfg({
        baseRate: s.baseRate != null ? String(s.baseRate) : '',
        currency: s.currency || 'USD',
        cutoffDay: s.cutoffDay ?? 28,
        active: s.active ?? true,
        effectiveDate: s.effectiveDate ? String(s.effectiveDate).slice(0, 10) : '',
      });
    } catch {
      // config is loaded lazily; failure leaves the form blank.
    }
  }, []);

  // Guards against out-of-order responses: the mount load() (default period)
  // and a load(periodKey) from a period change race; only the latest request's
  // response may update state, so a slow default response can't clobber a newer
  // explicit selection.
  const reqSeq = useRef(0);
  const load = useCallback(async (periodKey) => {
    const seq = (reqSeq.current += 1);
    setLoading(true);
    setError('');
    try {
      const data = await api.getSaasUsage(periodKey);
      if (seq !== reqSeq.current) return;
      setUsage(data);
      setPeriod(data.period.key);
    } catch (err) {
      if (seq !== reqSeq.current) return;
      setError(err.message || 'Failed to load SaaS usage billing.');
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadInvoices();
    loadConfig();
  }, [load, loadInvoices, loadConfig]);

  const invoiceRef = usage ? `INV-SAAS-${usage.period.key.replace('-', '')}` : '';

  const openInvoiceModal = () => {
    setInvoiceMsg(null);
    setEmailError('');
    setModalOpen(true);
  };

  const sendInvoice = async () => {
    setEmailError('');
    const email = recipientEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Please enter a valid billing email address.');
      return;
    }
    setSending(true);
    try {
      const res = await api.sendSaasInvoice({ period: usage.period.key, recipientEmail: email });
      await loadInvoices();
      setModalOpen(false);
      setInvoiceMsg({
        type: 'success',
        text: `Invoice ${res.invoice.invoiceRef} dispatched to ${res.invoice.recipientEmail} — status ${res.invoice.status}.`,
      });
    } catch (err) {
      setEmailError(err.message || 'Failed to send invoice.');
    } finally {
      setSending(false);
    }
  };

  const onPeriodChange = (e) => {
    const next = e.target.value;
    setPeriod(next);
    load(next);
  };

  const saveConfig = async () => {
    setCfgError('');
    setCfgMsg(null);
    const rate = cfg.baseRate.trim();
    if (!/^\d+(\.\d{1,2})?$/.test(rate) || parseFloat(rate) <= 0) {
      setCfgError('Enter a valid positive rate with up to two decimal places (e.g. 1.85).');
      return;
    }
    const cutoff = Number(cfg.cutoffDay);
    if (!Number.isInteger(cutoff) || cutoff < 1 || cutoff > 28) {
      setCfgError('Monthly cut-off day must be a whole number between 1 and 28.');
      return;
    }
    if (!cfg.effectiveDate) {
      setCfgError('An effective date is required.');
      return;
    }
    setCfgSaving(true);
    try {
      const s = await api.saveSaasBillingSettings({
        baseRate: parseFloat(rate),
        currency: cfg.currency,
        cutoffDay: cutoff,
        active: cfg.active,
        effectiveDate: cfg.effectiveDate,
      });
      setCfgMeta(s);
      setCfgMsg({ type: 'success', text: 'SaaS provider billing configuration saved.' });
      // Reflect the new rate in the active cycle figures.
      await load(period);
    } catch (err) {
      setCfgError(err.message || 'Failed to save SaaS billing configuration.');
    } finally {
      setCfgSaving(false);
    }
  };

  const deployments = useMemo(() => usage?.deployments || [], [usage]);

  // Historical Ledger: count-based KPIs derived from the loaded invoices.
  // Invoice objects expose status + deliveredAt (no monetary field), so the
  // summary cards are based on dispatch counts and delivery state, not amounts.
  const histKpis = useMemo(() => {
    const total = invoices.length;
    const dispatched = invoices.filter((inv) => inv.deliveredAt).length;
    const awaiting = total - dispatched;
    return { total, dispatched, awaiting };
  }, [invoices]);

  const histSearch = histQuery.trim().toLowerCase();
  const filteredInvoices = histSearch
    ? invoices.filter((inv) =>
        `${inv.invoiceRef || ''} ${inv.recipientEmail || ''}`.toLowerCase().includes(histSearch)
      )
    : invoices;

  const exportCsv = () => {
    if (!usage) return;
    const header = ['Job ID', 'Execution Date', 'Vessel Name', 'Movement Type', 'Tugs Deployed', 'Licensing Subtotal'];
    const escape = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [
      header.join(','),
      ...deployments.map((d) =>
        [d.jobId, formatDate(d.executionDate), d.vesselName, d.movementType, d.tugsDeployed, Number(d.subtotal).toFixed(2)]
          .map(escape)
          .join(',')
      ),
      ['', '', '', 'TOTAL', usage.totalUses, Number(usage.totalFee).toFixed(2)].map(escape).join(','),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `saas-usage-${usage.period.key}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const TABS = [
    { key: 'active', label: 'Active Cycle', icon: 'fa-calculator', testid: 'saas-tab-active' },
    { key: 'historical', label: 'Historical Ledger', icon: 'fa-history', testid: 'saas-tab-historical' },
    { key: 'config', label: 'Rate Configuration', icon: 'fa-cog', testid: 'saas-tab-config' },
  ];

  return (
    <div className="container-fluid py-4" data-testid="saas-billing-desk-page">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div
            className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm border-start border-success"
            style={{ borderLeftWidth: '4px' }}
          >
            <div>
              <h3 className="text-success mb-0">
                <i className="fas fa-file-invoice-dollar me-2" aria-hidden="true" />
                SaaS Provider Billing Desk
              </h3>
              <p className="text-muted mb-0 small">
                Platform administration: monitor the SaaS licensing charges owed by the tugboat company
                to the software provider, based on verified transaction usage.
              </p>
            </div>
            <span className="badge bg-success p-2" data-testid="saas-contract-status">
              <i className="fas fa-check-circle me-1" aria-hidden="true" />
              SaaS Contract Active
            </span>
          </div>
        </div>
      </div>

      {/* OPEX separation advisory (AC#4) */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="alert alert-info border-0 shadow-sm mb-0 d-flex align-items-center py-2 small" role="alert" data-testid="saas-opex-note">
            <i className="fas fa-info-circle me-2 text-info" aria-hidden="true" />
            <span className="text-secondary">
              <strong>Administrative Scope Boundary —</strong>{' '}
              {usage?.classificationNote ||
                'Software-provider usage licensing fees (OPEX), strictly segregated from customer-facing towage tariffs and SAP accounts receivable.'}
            </span>
          </div>
        </div>
      </div>

      {/* Tab navigation */}
      <ul className="nav nav-pills mb-4 bg-white rounded shadow-sm border p-1 d-inline-flex" role="tablist" data-testid="saas-tabs">
        {TABS.map((t) => (
          <li className="nav-item" key={t.key} role="presentation">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === t.key}
              className={`nav-link fw-semibold${activeTab === t.key ? ' active' : ''}`}
              onClick={() => setActiveTab(t.key)}
              data-testid={t.testid}
            >
              <i className={`fas ${t.icon} me-2`} aria-hidden="true" />
              {t.label}
            </button>
          </li>
        ))}
      </ul>

      {error && (
        <div className="alert alert-danger" role="alert" data-testid="saas-usage-error">
          {error}
        </div>
      )}

      {/* ---- TAB 1: Active Cycle ---- */}
      {activeTab === 'active' &&
        (loading ? (
          <div className="d-flex justify-content-center py-5">
            <div className="spinner-border text-success" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
          </div>
        ) : (
          usage && (
            <div className="row">
              {/* Monthly usage summary */}
              <div className="col-lg-4 mb-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white pt-4 border-bottom-0">
                    <h5 className="mb-0 text-secondary">
                      <i className="fas fa-calculator me-2 text-success" aria-hidden="true" />
                      Monthly Usage Summary
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <label htmlFor="saas-period" className="text-muted small fw-bold text-uppercase d-block mb-1">
                        Billing Period
                      </label>
                      <select
                        id="saas-period"
                        className="form-select"
                        value={period}
                        onChange={onPeriodChange}
                        data-testid="saas-period-select"
                      >
                        {(usage.availablePeriods.length
                          ? usage.availablePeriods
                          : [{ key: usage.period.key, label: usage.period.label }]
                        ).map((p) => (
                          <option key={p.key} value={p.key}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                      <span className="small text-muted d-block mt-1" data-testid="saas-period-label">
                        {usage.period.label}
                      </span>
                    </div>

                    <ul className="list-group list-group-flush mb-3 small">
                      <li className="list-group-item d-flex justify-content-between px-0 bg-transparent">
                        <span className="text-muted">Active SaaS Base Rate:</span>
                        <span className="fw-bold text-dark" data-testid="saas-rate">
                          {money(usage.rate, usage.currency)} per Tugboat Use
                        </span>
                      </li>
                      <li className="list-group-item d-flex justify-content-between px-0 bg-transparent">
                        <span className="text-muted">Tugboat Deployments (Qty):</span>
                        <span className="fw-bold text-primary" data-testid="saas-uses">
                          {usage.totalUses} Uses
                        </span>
                      </li>
                      <li className="list-group-item d-flex justify-content-between px-0 bg-transparent">
                        <span className="text-muted">Monthly Cycle Cut-off:</span>
                        <span className="fw-bold text-secondary">Day {usage.cutoffDay} of month</span>
                      </li>
                      <li className="list-group-item d-flex justify-content-between px-0 bg-transparent">
                        <span className="text-muted">Billing Currency:</span>
                        <span className="fw-bold text-secondary" data-testid="saas-currency">
                          {usage.currency}
                        </span>
                      </li>
                      <li className="list-group-item d-flex justify-content-between px-0 p-2 bg-light rounded border mt-2">
                        <span className="fw-bold text-dark mb-0">Calculated Software Fee:</span>
                        <span className="fw-bold text-success h5 mb-0" data-testid="saas-total">
                          {money(usage.totalFee, usage.currency)}
                        </span>
                      </li>
                    </ul>

                    <div className="mb-3">
                      <span className="small fw-bold text-muted d-block mb-1">Audit Ledger Verification Status</span>
                      <span
                        className={`badge w-100 text-start px-3 py-2 ${usage.active ? 'bg-success-subtle text-success' : 'bg-warning text-dark'}`}
                        data-testid="saas-verification"
                      >
                        <i className="fas fa-clock me-1" aria-hidden="true" />
                        {usage.active ? 'Active Billing Rule Applied' : 'Billing Rule Inactive'}
                      </span>
                    </div>

                    <button
                      className="btn btn-outline-primary w-100 fw-bold mb-2"
                      onClick={exportCsv}
                      disabled={!deployments.length}
                      data-testid="saas-export"
                    >
                      <i className="fas fa-file-excel me-1" aria-hidden="true" />
                      Export Usage Details (CSV)
                    </button>
                    <button
                      className="btn btn-success w-100 fw-bold"
                      onClick={openInvoiceModal}
                      data-testid="saas-generate-invoice"
                    >
                      <i className="fas fa-paper-plane me-1" aria-hidden="true" />
                      Generate SaaS Invoice
                    </button>

                    {invoiceMsg && (
                      <div className={`alert alert-${invoiceMsg.type} mt-3 mb-0 py-2 small`} role="alert" data-testid="saas-invoice-message">
                        {invoiceMsg.text}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Completed deployments */}
              <div className="col-lg-8 mb-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white pt-4 border-bottom-0 d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 text-secondary">
                      <i className="fas fa-list-ol me-2" aria-hidden="true" />
                      Completed Tugboat Deployment Log
                    </h5>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setShowDetails((v) => !v)}
                      data-testid="saas-view-details"
                      aria-expanded={showDetails}
                    >
                      <i className={`fas fa-${showDetails ? 'chevron-up' : 'chevron-down'} me-1`} aria-hidden="true" />
                      {showDetails ? 'Hide Usage Details' : 'View Usage Details'}
                    </button>
                  </div>
                  {showDetails && (
                    <div className="card-body p-0">
                      <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0 text-secondary small" data-testid="saas-deployments-table">
                          <thead className="bg-light text-secondary text-uppercase fw-bold">
                            <tr>
                              <th>Job ID</th>
                              <th>Execution Date</th>
                              <th>Vessel Name</th>
                              <th>Movement Type</th>
                              <th className="text-center">Tugs Deployed</th>
                              <th className="text-end">Licensing Subtotal</th>
                            </tr>
                          </thead>
                          <tbody>
                            {deployments.length === 0 ? (
                              <tr>
                                <td colSpan={6} className="text-center text-muted py-4" data-testid="saas-deployments-empty">
                                  No completed tugboat deployments in this billing period.
                                </td>
                              </tr>
                            ) : (
                              deployments.map((d) => (
                                <tr key={d.jobId} data-testid="saas-deployment-row">
                                  <td className="fw-bold text-dark">{d.jobId}</td>
                                  <td>{formatDate(d.executionDate)}</td>
                                  <td>{d.vesselName}</td>
                                  <td>{d.movementType}</td>
                                  <td className="text-center" data-testid="saas-row-tugs">
                                    {d.tugsDeployed}
                                  </td>
                                  <td className="text-end" data-testid="saas-row-subtotal">
                                    {money(d.subtotal, usage.currency)}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                          {deployments.length > 0 && (
                            <tfoot>
                              <tr className="bg-light fw-bold">
                                <td colSpan={4} className="text-end">
                                  Total
                                </td>
                                <td className="text-center" data-testid="saas-foot-uses">
                                  {usage.totalUses}
                                </td>
                                <td className="text-end text-success" data-testid="saas-foot-total">
                                  {money(usage.totalFee, usage.currency)}
                                </td>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        ))}

      {/* ---- TAB 2: Historical Ledger (delivery & SMTP tracking) ---- */}
      {activeTab === 'historical' && (
        <div data-testid="saas-historical-panel">
          {/* KPI summary cards (count-based; invoices carry no monetary field) */}
          <div className="row mb-4" data-testid="saas-hist-kpis">
            <div className="col-md-4 mb-3 mb-md-0">
              <div
                className="card border-0 shadow-sm bg-white h-100 p-3"
                style={{ borderLeft: '4px solid #007bff' }}
              >
                <div className="d-flex align-items-center">
                  <div
                    className="me-3 bg-light rounded-circle d-flex align-items-center justify-content-center text-primary"
                    style={{ width: 42, height: 42 }}
                  >
                    <i className="fas fa-file-invoice-dollar fa-lg" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-muted small text-uppercase fw-bold d-block mb-1">
                      Total Invoiced
                    </span>
                    <span className="h4 fw-bold text-dark d-block mb-0">
                      {histKpis.total} {histKpis.total === 1 ? 'Invoice' : 'Invoices'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4 mb-3 mb-md-0">
              <div
                className="card border-0 shadow-sm bg-white h-100 p-3"
                style={{ borderLeft: '4px solid #28a745' }}
              >
                <div className="d-flex align-items-center">
                  <div
                    className="me-3 bg-light rounded-circle d-flex align-items-center justify-content-center text-success"
                    style={{ width: 42, height: 42 }}
                  >
                    <i className="fas fa-paper-plane fa-lg" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-muted small text-uppercase fw-bold d-block mb-1">
                      Total Dispatched
                    </span>
                    <span className="h4 fw-bold text-success d-block mb-0">
                      {histKpis.dispatched} Delivered
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div
                className="card border-0 shadow-sm bg-white h-100 p-3"
                style={{ borderLeft: '4px solid #ffc107' }}
              >
                <div className="d-flex align-items-center">
                  <div
                    className="me-3 bg-light rounded-circle d-flex align-items-center justify-content-center text-warning"
                    style={{ width: 42, height: 42 }}
                  >
                    <i className="fas fa-clock fa-lg" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-muted small text-uppercase fw-bold d-block mb-1">
                      Awaiting Delivery
                    </span>
                    <span className="h4 fw-bold text-warning d-block mb-0">
                      {histKpis.awaiting} Pending
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm">
          <div className="card-header bg-white pt-4 border-bottom-0 d-md-flex justify-content-between align-items-center">
            <h5 className="mb-0 text-secondary">
              <i className="fas fa-history me-2" aria-hidden="true" />
              Invoice Delivery &amp; SMTP Tracking Log
            </h5>
            <div className="d-flex align-items-center mt-3 mt-md-0" style={{ gap: 10 }}>
              <div className="input-group input-group-sm" style={{ maxWidth: 220 }}>
                <span className="input-group-text bg-white">
                  <i className="fas fa-search text-muted" aria-hidden="true" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search ref or recipient…"
                  value={histQuery}
                  onChange={(e) => setHistQuery(e.target.value)}
                  data-testid="saas-hist-search"
                  aria-label="Search invoice delivery log"
                />
              </div>
              <span className="badge bg-light text-secondary border">API Integrations Active</span>
            </div>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0 text-secondary small" data-testid="saas-delivery-table">
                <thead className="bg-light text-secondary text-uppercase fw-bold">
                  <tr>
                    <th>Invoice Ref</th>
                    <th>Recipient</th>
                    <th>Timestamp</th>
                    <th className="text-end">Delivery Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center text-muted py-4" data-testid="saas-delivery-empty">
                        {invoices.length === 0
                          ? 'No invoices have been dispatched yet.'
                          : 'No invoices match your search.'}
                      </td>
                    </tr>
                  ) : (
                    filteredInvoices.map((inv) => (
                      <tr key={inv.id} data-testid="saas-delivery-row">
                        <td className="fw-bold text-dark">{inv.invoiceRef}</td>
                        <td>{inv.recipientEmail}</td>
                        <td>{inv.deliveredAt ? new Date(inv.deliveredAt).toLocaleString() : '—'}</td>
                        <td className="text-end">
                          <span className="badge bg-success" data-testid="saas-delivery-status">
                            <i className="fas fa-check-circle me-1" aria-hidden="true" />
                            {inv.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          </div>
        </div>
      )}

      {/* ---- TAB 3: Rate Configuration ---- */}
      {activeTab === 'config' && (
        <div className="row" data-testid="saas-config-panel">
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-cog me-2" aria-hidden="true" />
                  SaaS Provider Transaction Billing Rate
                </h5>
                <p className="text-muted small mb-0">
                  The per-tugboat-use rate applied to monthly usage. Changes are recorded in the
                  immutable audit ledger.
                </p>
              </div>
              <div className="card-body">
                {cfgError && (
                  <div className="alert alert-danger py-2" role="alert" data-testid="saas-config-error">
                    {cfgError}
                  </div>
                )}
                {cfgMsg && (
                  <div className={`alert alert-${cfgMsg.type} py-2`} role="alert" data-testid="saas-config-message">
                    {cfgMsg.text}
                  </div>
                )}
                <div className="row g-3">
                  <div className="col-md-6">
                    <label htmlFor="cfg-rate" className="form-label small fw-bold text-muted">
                      Base Rate per Tugboat Use <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">{cfg.currency}</span>
                      <input
                        id="cfg-rate"
                        type="text"
                        inputMode="decimal"
                        className="form-control"
                        value={cfg.baseRate}
                        placeholder="e.g. 1.85"
                        onChange={(e) => setCfg((p) => ({ ...p, baseRate: e.target.value }))}
                        data-testid="saas-config-rate"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="cfg-currency" className="form-label small fw-bold text-muted">
                      Billing Currency <span className="text-danger">*</span>
                    </label>
                    <select
                      id="cfg-currency"
                      className="form-select"
                      value={cfg.currency}
                      onChange={(e) => setCfg((p) => ({ ...p, currency: e.target.value }))}
                      data-testid="saas-config-currency"
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="SGD">SGD</option>
                      <option value="PHP">PHP</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="cfg-cutoff" className="form-label small fw-bold text-muted">
                      Monthly Cycle Cut-off Day <span className="text-danger">*</span>
                    </label>
                    <input
                      id="cfg-cutoff"
                      type="number"
                      min="1"
                      max="28"
                      className="form-control"
                      value={cfg.cutoffDay}
                      onChange={(e) => setCfg((p) => ({ ...p, cutoffDay: e.target.value }))}
                      data-testid="saas-config-cutoff"
                    />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="cfg-effective" className="form-label small fw-bold text-muted">
                      Effective Date <span className="text-danger">*</span>
                    </label>
                    <input
                      id="cfg-effective"
                      type="date"
                      className="form-control"
                      value={cfg.effectiveDate}
                      onChange={(e) => setCfg((p) => ({ ...p, effectiveDate: e.target.value }))}
                      data-testid="saas-config-effective"
                    />
                  </div>
                  <div className="col-md-6 d-flex align-items-end">
                    <div className="form-check form-switch mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="cfg-active"
                        checked={cfg.active}
                        onChange={(e) => setCfg((p) => ({ ...p, active: e.target.checked }))}
                        data-testid="saas-config-active"
                      />
                      <label className="form-check-label small" htmlFor="cfg-active">
                        Billing Rule {cfg.active ? 'Active' : 'Inactive'}
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6 d-flex align-items-end">
                    <button
                      className="btn btn-primary w-100"
                      onClick={saveConfig}
                      disabled={cfgSaving}
                      data-testid="saas-config-save"
                    >
                      {cfgSaving && (
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      )}
                      <i className="fas fa-save me-1" aria-hidden="true" />
                      Save Rate Configuration
                    </button>
                  </div>
                </div>
                {cfgMeta?.updatedBy && (
                  <p className="text-muted small mb-0 mt-3" data-testid="saas-config-meta">
                    Last updated by {cfgMeta.updatedBy}
                    {cfgMeta.updatedAt ? ` on ${new Date(cfgMeta.updatedAt).toLocaleString()}` : ''}.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Preview & Dispatch Modal */}
      {modalOpen && usage && (
        <div
          className="modal d-block"
          tabIndex={-1}
          role="dialog"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          data-testid="saas-invoice-modal"
        >
          <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header bg-dark text-white py-3">
                <h5 className="modal-title fw-bold text-uppercase small">
                  <i className="fas fa-file-invoice-dollar me-2 text-success" aria-hidden="true" />
                  SaaS Software Provider Licensing Invoice
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  aria-label="Close"
                  onClick={() => setModalOpen(false)}
                  data-testid="saas-modal-close"
                />
              </div>
              <div className="modal-body p-4">
                <div className="d-flex justify-content-between align-items-start border-bottom pb-3 mb-3">
                  <div>
                    <h5 className="fw-bold text-success mb-1">
                      <i className="fas fa-anchor me-2" aria-hidden="true" />
                      TugOS
                    </h5>
                    <span className="text-muted small">SaaS Platform Operator &amp; Provider</span>
                  </div>
                  <div className="text-end">
                    <span className="h5 fw-bold text-dark d-block mb-1" data-testid="saas-modal-ref">
                      {invoiceRef}
                    </span>
                    <span className="text-muted d-block small">{usage.period.label}</span>
                  </div>
                </div>

                <table className="table table-bordered small">
                  <thead className="bg-light">
                    <tr>
                      <th>Licensing Description</th>
                      <th className="text-center">Contract Base Rate</th>
                      <th className="text-center">Completed Deployments</th>
                      <th className="text-end">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <strong>Tugboat Operational Deployed Runs</strong>
                        <span className="d-block text-muted small">
                          Software usage auto-compiled from verified deployment logs.
                        </span>
                      </td>
                      <td className="text-center">{money(usage.rate, usage.currency)}</td>
                      <td className="text-center">{usage.totalUses}</td>
                      <td className="text-end fw-bold">{money(usage.totalFee, usage.currency)}</td>
                    </tr>
                    <tr>
                      <td colSpan={3} className="text-end fw-bold">
                        Taxes / VAT (0% – SaaS Export)
                      </td>
                      <td className="text-end">{money(0, usage.currency)}</td>
                    </tr>
                    <tr className="bg-light">
                      <td colSpan={3} className="text-end fw-bold text-dark">
                        Total SaaS Invoice Amount
                      </td>
                      <td className="text-end fw-bold text-success" data-testid="saas-modal-total">
                        {money(usage.totalFee, usage.currency)}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div className="mb-2">
                  <label htmlFor="saas-recipient" className="form-label small fw-bold text-muted">
                    Recipient Billing Email <span className="text-danger">*</span>
                  </label>
                  <input
                    id="saas-recipient"
                    type="email"
                    className={`form-control${emailError ? ' is-invalid' : ''}`}
                    value={recipientEmail}
                    onChange={(e) => {
                      setRecipientEmail(e.target.value);
                      if (emailError) setEmailError('');
                    }}
                    data-testid="saas-recipient-email"
                  />
                  {emailError && (
                    <div className="invalid-feedback d-block" data-testid="saas-email-error">
                      {emailError}
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer bg-light py-2">
                <button
                  type="button"
                  className="btn btn-secondary fw-bold text-uppercase"
                  onClick={() => setModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success fw-bold text-uppercase"
                  onClick={sendInvoice}
                  disabled={sending}
                  data-testid="saas-send-invoice"
                >
                  {sending && (
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  )}
                  <i className="fas fa-paper-plane me-1" aria-hidden="true" />
                  Send Invoice to Client
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
